import { mapGetters } from 'vuex';
import dayjs from '@/localPlugins/dayjs';
import get from 'lodash/get';
import difference from 'lodash/difference';
import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';
import SegmentSelect from '@/components/SegmentsSelect/SegmentsSelect.vue';
import CollapsibleSelect from '@/components/CollapsibleSelect/CollapsibleSelect.vue';
import { dropDownToSelected, filterEndpointToMetaData, fetchFilterOptions, formatFilterData } from '../../../helpers/filterHelper';
import {
  datePickerDefaultOptions,
  defaultDateRange,
  breakdownOptions,
  filterBreakdownOptions,
  getNonReactiveDateRange,
} from '../../../configs/dateRange';
import { limitedString } from '@/libs/stringUtils';
import set from 'lodash/set';
import findIndex from 'lodash/findIndex';

const defaultArrayConfig = [];

export default {
  name: 'selectableFiltersMixin',
  components: {
    SegmentSelect,
    CollapsibleSelect,
  },
  data() {
    return {
      selectedFilters: [],
      IdsOfSelectedSegmentFilter: [],
      dateRange: defaultDateRange(),
      // dateRange holds the date selected by the user.
      // if the date range is greater than 90 days, we present the user with a modal
      // informing them that selecting a large date range may affect load time.
      // if the user clicks ok - the requests is initiated
      // if the user clicks cancels - the date range is reverted to the previous date range.
      previousDateRange: defaultDateRange(),
      selectedBreakdownOption: breakdownOptions.find(
        ({ isDefault }) => isDefault,
      ),
      segmentAlias: '',
      defaultFilterTypesShown: defaultArrayConfig,
      defaultAgentFilterOptions: defaultArrayConfig,
      defaultPrimaryAgentFilterOptions: defaultArrayConfig,
      defaultCategoryFilterOptions: defaultArrayConfig,
      defaultTagFilterOptions: defaultArrayConfig,
      defaultAgentsTeamsFilterOptions: defaultArrayConfig,
      defaultTeamFilterOptions: defaultArrayConfig,
      AgentFilterOptions: defaultArrayConfig,
      CategoryFilterOptions: defaultArrayConfig,
      customerSegmentFilterOptions: defaultArrayConfig,
      tagFilterOptions: defaultArrayConfig,
      agentsTeamsFilterOptions: defaultArrayConfig,
      TeamFilterOptions: defaultArrayConfig,
      loadedFilterEndpoints: {},
      agentsFilter: false,
      agentsTeamsFilter: false,
      teamsFilter: false,
      categoriesFilter: false,
      customerSegmentsFilter: false,
      tagsFilter: false,
      primaryAgentsFilter: false,
      FilterTypeOptions: [
        // label is the display text on UI
        // name is used to find configuration
        // endpoint is the API endpoint for loading filter options
        { id: 1, name: 'Agents', label: 'Agents', endpoint: 'metrics_by_agent' },
        { id: 2, name: 'Agents Teams', label: 'Agents By Teams', endpoint: 'all_teams' },
        { id: 3, name: 'Teams', label: 'Teams', endpoint: 'metrics_by_team' },
        { id: 4, name: 'Categories', label: 'Categories', endpoint: 'metrics_by_category' },
        { id: 5, name: 'Customer Segments', label: 'Segments', endpoint: 'metrics_by_customer_segment' },
        { id: 6, name: 'Tags', label: 'Tags', endpoint: 'metrics_by_tag' },
        { id: 7, name: 'Primary Agents', label: 'Primary Agents', endpoint: 'metrics_by_primary_agent' },
      ],
    };
  },
  computed: {
    ...mapGetters({
      myTeams: 'agent/teams',
      customerSegmentNameFilterOptions: 'configs/segments',
    }),
    datePickerOptions() {
      return datePickerDefaultOptions();
    },
    dateRangeUtc() {
      return this.previousDateRange.map(date => dayjs(date).unix());
    },
    filteredBreakdownOptions() {
      return filterBreakdownOptions(this.dateRange, breakdownOptions);
    },
  },
  methods: {
    optionLabel(option) {
      return limitedString(option.name);
    },
    async onFilterTypeChange(chosenFilterTypeOptions) {
      const unchosenFilterTypeOptions = difference(this.FilterTypeOptions, chosenFilterTypeOptions);
      this.showFilters(chosenFilterTypeOptions, true);
      this.showFilters(unchosenFilterTypeOptions, false);
      this.clearFilters(unchosenFilterTypeOptions);
      // Lazy load filter options for newly selected filter types
      for (const filterOption of chosenFilterTypeOptions) {
        const { endpoint } = filterOption;
        if (endpoint && !this.loadedFilterEndpoints[endpoint]) {
          this.loadedFilterEndpoints[endpoint] = true;
          await this.loadFilterOption(endpoint);
        }
      }
    },
    setSelectedFilters(filterName, chosenFilters) {
      this.selectedFilters = dropDownToSelected(chosenFilters, this.selectedFilters, filterName);
    },

    onAgentFilterChange(chosenFilters) {
      this.setSelectedFilters('agents', chosenFilters);
    },
    onPrimaryAgentFilterChange(chosenFilters) {
      this.setSelectedFilters('primary_agents', chosenFilters);
    },
    onCategoryFilterChange(chosenFilters) {
      this.setSelectedFilters('categories', chosenFilters);
    },
    onTeamFilterChange(chosenFilters) {
      this.setSelectedFilters('teams', chosenFilters);
    },
    onCustomerSegmentFilterChange(chosenFilters) {
      this.setSelectedFilters('customer_segments', chosenFilters);
    },
    setSelectedFiltersToNames(filterType) {
      const filterValuesPath = `0.${filterType}`;
      const selectedFilterOptionsIdxs = get(this.selectedFilters, filterValuesPath, [])
        .map(selectedId => findIndex(this.customerSegmentNameFilterOptions,
          (option) => option.id === selectedId));
      const selectedFilterNames = selectedFilterOptionsIdxs
        .map(cid => get(this.customerSegmentNameFilterOptions[cid],
          'name',
          ''));
      set(this.selectedFilters,
        filterValuesPath, selectedFilterNames);
    },
    setSelectedFiltersWithSegmentIds(chosenSegmentIds) {
      this.IdsOfSelectedSegmentFilter = chosenSegmentIds;
    },
    onCustomerSegmentNameFilterChange(chosenFilters) {
      const chosenIdNames = chosenFilters
        .map(chosenValue => ({
          id: chosenValue.id,
          name: chosenValue.name }));
      this.setSelectedFilters('customer_segment_names', chosenIdNames);
      this.setSelectedFiltersToNames('customer_segment_names');
      const chosenSegmentIds = chosenFilters.map(chosenValue => chosenValue.id);
      this.setSelectedFiltersWithSegmentIds(chosenSegmentIds);

    },
    onTagFilterChange(chosenFilters) {
      this.setSelectedFilters('tags', chosenFilters);
    },
    onAgentsTeamsFilterChange(chosenFilters) {
      this.setSelectedFilters('agents_teams', chosenFilters);
    },
    clearFilters(filterTypeOptions) {
      // eslint-disable-next-line array-callback-return
      filterTypeOptions.map(filterOption => {
        this.setSelectedFilters(snakeCase(filterOption.name), []);
      });
    },
    showFilters(filterTypeOptions, shouldShow) {
      // eslint-disable-next-line array-callback-return
      filterTypeOptions.map(filterOption => {
        this[camelCase(`${filterOption.name} Filter`)] = shouldShow;
      });
    },
    async loadFilterOption(endpoint) {
      if (!{}.hasOwnProperty.call(filterEndpointToMetaData, endpoint)) {
        return;
      }
      let datapoints;
      const metaData = filterEndpointToMetaData[endpoint];
      if (metaData.remoteCall) {
        datapoints = await fetchFilterOptions(this, endpoint);
      } else {
        datapoints = get(this, metaData.getterName, []);
      }
      const formattedOptionData = formatFilterData(datapoints, metaData.getFormattedName);
      metaData.setOptionsStateVariable(this, formattedOptionData);
    },
    async loadAllFilterOptions() {
      for (const endpoint in filterEndpointToMetaData) {
        if ({}.hasOwnProperty.call(filterEndpointToMetaData, endpoint)) {
        /* eslint-disable no-await-in-loop */
          await this.loadFilterOption(endpoint);
        }
      }
    },
    handleDatePickerChange(newDateRange) {
      this.previousDateRange = getNonReactiveDateRange(newDateRange);
    },
    async onSegmentHover(segment) {
      if (segment) {
        const segmentDetails = await this.$store.dispatch('configs/getCustomerSegmentDetails', { id: segment.id });
        this.segmentAlias = get(segmentDetails, 'payload.alias', '');
      } else {
        this.segmentAlias = '';
      }
    },
  },
};
